if (!customElements.get("nmn-cart-progress")) {
  customElements.define("nmn-cart-progress", class extends HTMLElement {
    connectedCallback() {
      const conversionRate = Number(window.Shopify?.currency?.rate) || 1;
      const total = Number(this.dataset.total);
      let previousThreshold = 0;
      this.querySelectorAll("[data-threshold]").forEach((milestone) => {
        const threshold = Math.max(previousThreshold, Number(milestone.dataset.threshold) * conversionRate);
        const span = Math.max(1, threshold - previousThreshold);
        const fill = Math.max(0, Math.min(100, (total - previousThreshold) * 100 / span));
        const complete = total >= threshold;
        milestone.style.setProperty("--nmn-cart-fill", `${fill}%`);
        milestone.toggleAttribute("data-complete", complete);
        const status = milestone.querySelector("[data-milestone-status]");
        status.textContent = complete ? this.dataset.reached : this.dataset.notReached;
        previousThreshold = threshold;
      });
    }
  });
}

if (!customElements.get("nmn-cart-upsells")) {
  customElements.define("nmn-cart-upsells", class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.cards = [];
      this.position = 0;
      this.addEventListener("click", (event) => {
        const button = event.target.closest("[data-upsell-direction]");
        if (!button || this.cards.length < 2) return;
        this.position = (this.position + Number(button.dataset.upsellDirection) + this.cards.length) % this.cards.length;
        this.update();
      }, { signal });
      this.addEventListener("change", (event) => {
        const select = event.target.closest("[data-upsell-variant]");
        if (!select) return;
        const option = select.selectedOptions[0];
        const card = select.closest("[data-upsell-card]");
        card.querySelector("[data-upsell-price]").textContent = option.dataset.price;
        card.querySelector('[name="quantity"]').value = option.dataset.min || "1";
      }, { signal });
      this.addEventListener("submit", (event) => {
        if (this.closest("cart-drawer")?.mutationPending) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
      this.loadRecommendations(signal);
    }

    async loadRecommendations(signal) {
      this.hidden = true;
      const container = this.querySelector(".nmn-cart-drawer__upsell-cards");
      if (!container || !this.dataset.url) return;
      container.replaceChildren();
      const productIds = JSON.parse(this.dataset.productIds || "[]").map(String);
      const excludedIds = new Set(productIds);
      const limit = Math.max(1, Math.min(6, Number(this.dataset.limit) || 2));
      const cards = [];

      // Request the full API allowance before filtering so cart items don't consume display slots.
      for (const productId of productIds) {
        if (signal.aborted || cards.length >= limit) break;
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set("section_id", this.dataset.sectionId);
        url.searchParams.set("product_id", productId);
        url.searchParams.set("limit", "10");
        url.searchParams.set("intent", this.dataset.intent || "complementary");
        try {
          const response = await fetch(url, { signal });
          if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), "text/html");
          for (const card of html.querySelectorAll("[data-upsell-card]")) {
            const id = card.dataset.productId;
            if (!id || excludedIds.has(id)) continue;
            excludedIds.add(id);
            cards.push(card);
            if (cards.length >= limit) break;
          }
        } catch (error) {
          if (signal.aborted) return;
          console.warn("Cart recommendations could not load:", error);
        }
      }

      if (signal.aborted || !this.isConnected) return;
      this.cards = cards;
      this.update();
      container.replaceChildren(...cards);
      this.querySelectorAll("[data-upsell-direction]").forEach((button) => {
        button.hidden = cards.length < 2;
      });
      this.hidden = cards.length === 0;
    }

    update() {
      this.cards.forEach((card, index) => { card.hidden = index !== this.position; });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  });
}

if (!customElements.get("nmn-cart-subscription")) {
  customElements.define("nmn-cart-subscription", class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.querySelectorAll("[data-plan-upgrade], [data-plan-select]").forEach((control) => { control.hidden = false; });
      this.addEventListener("click", (event) => {
        const button = event.target.closest("[data-plan-upgrade]");
        if (button) this.changePlan(button.dataset.planUpgrade, button);
      }, { signal });
      this.addEventListener("change", (event) => {
        const select = event.target.closest("[data-plan-select]");
        if (!select) return;
        // The Dawn quantity listener must not treat this select as a quantity input.
        event.stopPropagation();
        this.changePlan(select.value, select);
      }, { signal });
    }

    async changePlan(planId, control) {
      const drawer = this.closest("cart-drawer");
      if (!drawer || drawer.mutationPending) return;
      drawer.mutationPending = true;
      drawer.setAttribute("aria-busy", "true");
      const error = this.querySelector("[data-plan-error]");
      error.hidden = true;
      const controls = Array.from(drawer.querySelectorAll("input, select, button:not([data-cart-close])"));
      const disabledStates = controls.map((input) => input.disabled);
      controls.forEach((input) => { input.disabled = true; });
      const cartItems = drawer.querySelector("cart-drawer-items");
      const deferred = cartItems.createCartLinesUpdateEvent("update", this.dataset.variantId, Number(this.dataset.quantity), this.dataset.lineKey);
      try {
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: this.dataset.lineKey,
            quantity: Number(this.dataset.quantity),
            selling_plan: planId ? Number(planId) : null,
            sections: drawer.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status) throw new Error(cart.description || cart.errors || window.cartStrings.error);
        cartItems.resolveCartLinesUpdate(deferred, cart);
        drawer.renderContents(cart);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: cart });
      } catch (exception) {
        error.textContent = exception.message || window.cartStrings.error;
        error.hidden = false;
        if (control.matches("[data-plan-select]")) control.value = control.querySelector("option[selected]")?.value || "";
        cartItems.dispatchCartErrorEvent(error.textContent, "INVALID");
        deferred?.reject(exception);
      } finally {
        controls.forEach((input, index) => { if (input.isConnected) input.disabled = disabledStates[index]; });
        drawer.mutationPending = false;
        drawer.removeAttribute("aria-busy");
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  });
}
